/**
 * Reusable photo capture field for Safety forms.
 * Unlike fuel/petty-cash (camera-only, anti-fraud), safety observations CAN be
 * documented from the gallery too — incidents are often photographed first and
 * reported later. So we allow both camera and gallery selection.
 */
import React, { useRef } from 'react';
import { Camera, X } from 'lucide-react';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function PhotoCaptureField({ photos, setPhotos, max = 6 }) {
  const inputRef = useRef(null);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = max - photos.length;
    const picked = files.slice(0, Math.max(0, remaining));
    const b64s = await Promise.all(picked.map(fileToBase64));
    setPhotos([...photos, ...b64s]);
    e.target.value = '';
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {photos.map((p, i) => (
          <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden border border-slate-200">
            <img src={p} alt={`photo ${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
              className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
              data-testid={`photo-remove-${i}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-20 h-20 rounded-sm border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-[#3b4872] hover:text-[#3b4872] transition-colors"
            data-testid="photo-add-btn"
          >
            <Camera size={20} />
            <span className="text-[9px] mt-1 font-display font-bold uppercase">Add</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onPick}
        className="hidden"
        data-testid="photo-input"
      />
      <p className="text-[10px] text-slate-400">Camera or gallery · up to {max} photos</p>
    </div>
  );
}
